import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createGlobalStyle } from 'styled-components';

export type FlyTargetKey = 'essence' | 'monetary' | 'card';

type FlyGroupConfig = {
  id: string;
  destination: FlyTargetKey;
  names: string[];
  durationMs: number;
  staggerMs: number;
  element: HTMLElement | null;
};

type FlyTargetContextValue = {
  registerTarget: (key: FlyTargetKey, element: HTMLElement | null) => void;
  registerGroup: (config: FlyGroupConfig) => void;
  unregisterGroup: (id: string) => void;
  hasGroupsIn: (dialogRoot: HTMLElement | null) => boolean;
  prepareFlyOut: (dialogRoot: HTMLElement | null) => boolean;
  clearFlyOut: () => void;
};

const FlyTargetContext = createContext<FlyTargetContextValue | null>(null);

export const useFlyTargetContext = () => useContext(FlyTargetContext);

export const useRegisterFlyTarget = <T extends HTMLElement>(
  key: FlyTargetKey | undefined
) => {
  const context = useFlyTargetContext();
  const ref = useRef<T>(null);

  useEffect(() => {
    if (!key) return undefined;
    context?.registerTarget(key, ref.current);
    return () => context?.registerTarget(key, null);
  }, [context, key]);

  return ref;
};

const GROUP_REMOVAL_DELAY_MS = 2_000;

const resolveVisibleTargetCenter = (
  element: HTMLElement | null,
  dialogRoot: HTMLElement
) => {
  if (!element) return null;

  const rect = element.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return null;

  if (
    element.checkVisibility &&
    !element.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })
  ) {
    return null;
  }

  const x = rect.left + rect.width / 2;
  const y = rect.top + rect.height / 2;
  if (x < 0 || y < 0 || x > window.innerWidth || y > window.innerHeight) {
    return null;
  }

  const stack = document.elementsFromPoint(x, y);
  const targetIndex = stack.findIndex(
    node => node === element || element.contains(node)
  );
  if (targetIndex === -1) return null;

  const occludedByOther = stack
    .slice(0, targetIndex)
    .some(node => !dialogRoot.contains(node));
  if (occludedByOther) return null;

  return { x, y };
};

const FLY_REGROUP_MS = 600;
const FLY_PAUSE_MS = 250;
const FLY_MOVE_MS = 800;
export const FLY_DURATION_MS = FLY_REGROUP_MS + FLY_PAUSE_MS + FLY_MOVE_MS;

const regroupEndPct = ((FLY_REGROUP_MS / FLY_DURATION_MS) * 100).toFixed(2);
const moveStartPct = (
  ((FLY_REGROUP_MS + FLY_PAUSE_MS) / FLY_DURATION_MS) *
  100
).toFixed(2);

const centeredTransform = `translate(var(--mtt-cx), var(--mtt-cy))
  translate(var(--mtt-ox), var(--mtt-oy)) rotate(var(--mtt-rot)) scale(0.8)
  translate(-50%, -50%)`;

const FlyTargetStyle = createGlobalStyle<{ $groups: FlyGroupConfig[] }>`
  @keyframes mtt-fly {
    ${regroupEndPct}% {
      transform: ${centeredTransform};
      opacity: 1;
      animation-timing-function: linear;
    }
    ${moveStartPct}% {
      transform: ${centeredTransform};
      opacity: 1;
    }
    99% {
      opacity: 1;
    }
    100% {
      transform: translate(calc(var(--mtt-x) - 50%), calc(var(--mtt-y) - 50%)) rotate(var(--mtt-rot))
        scale(0.1);
      opacity: 0;
    }
  }

  ${({ $groups }) =>
    $groups
      .flatMap(group =>
        group.names.map((name, index) => {
          const delay = index * group.staggerMs;
          const mid = (group.names.length - 1) / 2;
          const rotation = (Math.sin(index * 137) * 5).toFixed(1);
          const offsetX = Math.round((index - mid) * 4);
          const offsetY = Math.round((index - mid) * 4);
          return `
            :root:active-view-transition-type(mtt-fly-out)::view-transition-group(${name}) {
              --mtt-x: var(--mtt-${group.destination}-x);
              --mtt-y: var(--mtt-${group.destination}-y);
              --mtt-rot: ${rotation}deg;
              --mtt-ox: ${offsetX}px;
              --mtt-oy: ${offsetY}px;
              animation-name: var(--mtt-${group.destination}-name, none);
              animation-duration: ${group.durationMs}ms;
              animation-delay: ${delay}ms;
              animation-timing-function: var(--ease-in-out-quint);
              animation-fill-mode: both;
            }
            :root:active-view-transition-type(mtt-fly-out)::view-transition-old(${name}) {
              animation-name: none;
            }
          `;
        })
      )
      .join('\n')}
`;

export const FlyTargetProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const targetsRef = useRef(new Map<FlyTargetKey, HTMLElement>());
  const groupsRef = useRef<FlyGroupConfig[]>([]);
  const removalTimersRef = useRef(
    new Map<string, ReturnType<typeof setTimeout>>()
  );
  const armedRef = useRef(new Set<FlyTargetKey>());
  const [groups, setGroups] = useState<FlyGroupConfig[]>([]);

  const registerTarget = useCallback(
    (key: FlyTargetKey, element: HTMLElement | null) => {
      if (element) targetsRef.current.set(key, element);
      else targetsRef.current.delete(key);
    },
    []
  );

  const registerGroup = useCallback((config: FlyGroupConfig) => {
    const pending = removalTimersRef.current.get(config.id);
    if (pending) {
      clearTimeout(pending);
      removalTimersRef.current.delete(config.id);
    }
    groupsRef.current = [
      ...groupsRef.current.filter(g => g.id !== config.id),
      config,
    ];
    setGroups(groupsRef.current);
  }, []);

  const unregisterGroup = useCallback((id: string) => {
    const timer = setTimeout(() => {
      groupsRef.current = groupsRef.current.filter(g => g.id !== id);
      setGroups(groupsRef.current);
      removalTimersRef.current.delete(id);
    }, GROUP_REMOVAL_DELAY_MS);
    removalTimersRef.current.set(id, timer);
  }, []);

  const hasGroupsIn = useCallback(
    (dialogRoot: HTMLElement | null) =>
      !!dialogRoot &&
      groupsRef.current.some(
        group => group.element && dialogRoot.contains(group.element)
      ),
    []
  );

  const prepareFlyOut = useCallback((dialogRoot: HTMLElement | null) => {
    if (!dialogRoot) return false;

    const root = document.documentElement;
    root.style.setProperty('--mtt-cx', `${window.innerWidth / 2}px`);
    root.style.setProperty('--mtt-cy', `${window.innerHeight / 2}px`);

    const armed = new Set<FlyTargetKey>();

    groupsRef.current.forEach(group => {
      if (!group.element || !dialogRoot.contains(group.element)) return;
      if (armed.has(group.destination)) return;

      const center = resolveVisibleTargetCenter(
        targetsRef.current.get(group.destination) ?? null,
        dialogRoot
      );
      if (!center) {
        root.style.removeProperty(`--mtt-${group.destination}-name`);
        return;
      }

      root.style.setProperty(`--mtt-${group.destination}-x`, `${center.x}px`);
      root.style.setProperty(`--mtt-${group.destination}-y`, `${center.y}px`);
      root.style.setProperty(`--mtt-${group.destination}-name`, 'mtt-fly');
      armed.add(group.destination);
    });

    armedRef.current = armed;

    return armed.size > 0;
  }, []);

  const clearFlyOut = useCallback(() => {
    const root = document.documentElement;
    armedRef.current.forEach(key =>
      root.style.removeProperty(`--mtt-${key}-name`)
    );
    armedRef.current = new Set();
  }, []);

  const value = useMemo(
    () => ({
      registerTarget,
      registerGroup,
      unregisterGroup,
      hasGroupsIn,
      prepareFlyOut,
      clearFlyOut,
    }),
    [
      registerTarget,
      registerGroup,
      unregisterGroup,
      hasGroupsIn,
      prepareFlyOut,
      clearFlyOut,
    ]
  );

  return (
    <FlyTargetContext value={value}>
      <FlyTargetStyle $groups={groups} />
      {children}
    </FlyTargetContext>
  );
};
